import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface GoogleAd {
  id: string;
  ad_name: string;
  split_test: string;
  assets_link: string;
  ad_link: string;
  runtime: string;
  leads: number;
  last_updated: string;
  created_by: string;
}

interface GoogleAdsMetricsProps {
  ads: GoogleAd[];
  onAddAd: (ad: Omit<GoogleAd, 'id' | 'last_updated' | 'leads' | 'created_by'>) => void;
  onUpdateAd: (id: string, field: string, value: string | number) => void;
  onDeleteAd?: (id: string) => void;
}

const formatDuration = (startTime: string) => {
  if (!startTime) return 'N/A';
  
  try {
    const start = new Date(startTime).getTime();
    const now = new Date().getTime();
    const diff = now - start;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  } catch (e) {
    return 'N/A';
  }
};

const GoogleAdsMetrics: React.FC<GoogleAdsMetricsProps> = ({ ads, onAddAd, onUpdateAd, onDeleteAd }) => {
  const [open, setOpen] = React.useState(false);
  const [editingAd, setEditingAd] = React.useState<GoogleAd | null>(null);
  const [newAd, setNewAd] = React.useState({
    ad_name: '',
    split_test: 'false',
    assets_link: '',
    ad_link: '',
    runtime: '',
  });

  const handleAddAd = () => {
    if (!newAd.ad_name.trim()) return;
    onAddAd(newAd);
    setNewAd({
      ad_name: '',
      split_test: 'false',
      assets_link: '',
      ad_link: '',
      runtime: '',
    });
    setOpen(false);
  };

  const handleUpdateField = (field: string, value: string) => {
    if (!editingAd) return;
    onUpdateAd(editingAd.id, field, value);
  };

  const handleDeleteAd = (id: string) => {
    if (onDeleteAd) {
      onDeleteAd(id);
    }
  };

  return (
    <div className="space-y-6 mb-12">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Active Google Ads</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gray-900 text-white hover:bg-gray-800 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Add Google Ad
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Google Ad</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Ad Name</Label>
                <Input
                  value={newAd.ad_name}
                  onChange={(e) => setNewAd(prev => ({ ...prev, ad_name: e.target.value }))}
                  placeholder="Enter ad name"
                  required
                />
              </div>
              <div className="space-y-2 flex items-center">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="splitTest"
                    checked={newAd.split_test === 'true'}
                    onCheckedChange={(checked) => 
                      setNewAd(prev => ({ ...prev, split_test: checked ? 'true' : 'false' }))
                    }
                  />
                  <Label htmlFor="splitTest">Split Test</Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Assets Link</Label>
                <Input
                  type="url"
                  value={newAd.assets_link}
                  onChange={(e) => setNewAd(prev => ({ ...prev, assets_link: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Ad Link</Label>
                <Input
                  type="url"
                  value={newAd.ad_link}
                  onChange={(e) => setNewAd(prev => ({ ...prev, ad_link: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Runtime</Label>
                <Input
                  type="datetime-local"
                  value={newAd.runtime}
                  onChange={(e) => setNewAd(prev => ({ ...prev, runtime: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <Button 
                  className="w-full bg-gray-900 text-white hover:bg-gray-800"
                  onClick={handleAddAd}
                >
                  Add Ad
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ads.map(ad => {
          const isRunning = ad.runtime && ad.runtime !== '';
          
          return (
            <Card key={ad.id} className="p-6 hover:shadow-md transition-shadow duration-200 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <div className="relative">
                  <span className="absolute inset-0 animate-pulse rounded-full bg-purple-400/20 blur-sm"></span>
                  <span className="relative inline-flex items-center px-3 py-1 text-xs font-medium text-purple-900 bg-purple-100 rounded-full border border-purple-200">
                    Active Campaign
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 mb-4">
                <div className="relative flex items-center">
                  <div className={`w-3 h-3 rounded-full ${isRunning ? 'bg-[#7ed957]' : 'bg-gray-300'}`}>
                    {isRunning && (
                      <div className="absolute w-3 h-3 rounded-full bg-[#7ed957] animate-ping" 
                           style={{ backgroundColor: '#7ed957', opacity: '0.5' }} />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-2">{ad.ad_name || 'Untitled Ad'}</h3>
                </div>
                {ad.split_test === 'true' && (
                  <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full whitespace-nowrap">
                    Split Test Active
                  </span>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col items-center text-center">
                    <Label className="text-xs text-gray-500">Runtime</Label>
                    <p className="font-medium whitespace-nowrap">
                      {isRunning ? formatDuration(ad.runtime) : 'N/A'}
                    </p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <Label className="text-xs text-gray-500">Total Leads</Label>
                    <p className="font-medium">{ad.leads || 0}</p>
                  </div>
                </div>
                
                <div className="flex justify-center">
                  <div className="flex flex-col items-center text-center">
                    <Label className="text-xs text-gray-500 mb-2">Ad Link</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(ad.ad_link || '#', '_blank')}
                      disabled={!ad.ad_link}
                      className="w-32"
                    >
                      View Ad
                    </Button>
                  </div>
                </div>
              </div>

              <div className="absolute top-4 right-4 flex items-center gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => setEditingAd(ad)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Edit Ad</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label>Ad Name</Label>
                        <Input
                          value={ad.ad_name}
                          onChange={(e) => handleUpdateField('ad_name', e.target.value)}
                          placeholder="Enter ad name"
                        />
                      </div>
                      <div className="space-y-2 flex items-center">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`splitTest-${ad.id}`}
                            checked={ad.split_test === 'true'}
                            onCheckedChange={(checked) => 
                              handleUpdateField('split_test', checked ? 'true' : 'false')
                            }
                          />
                          <Label htmlFor={`splitTest-${ad.id}`}>Split Test</Label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Assets Link</Label>
                        <Input
                          type="url"
                          value={ad.assets_link || ''}
                          onChange={(e) => handleUpdateField('assets_link', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Ad Link</Label>
                        <Input
                          type="url"
                          value={ad.ad_link || ''}
                          onChange={(e) => handleUpdateField('ad_link', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Runtime</Label>
                        <Input
                          type="datetime-local"
                          value={ad.runtime || ''}
                          onChange={(e) => handleUpdateField('runtime', e.target.value)}
                        />
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Ad</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{ad.ad_name}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => handleDeleteAd(ad.id)}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default GoogleAdsMetrics;
